import { Input, Button, Badge, Checkbox } from "antd";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import CardPartial from "../partials/Card";
import { UserFormInterface } from "../definitions";
import type { InputRef } from "antd";

interface City {
  uuid: string;
  name: string;
}

interface Step5Props {
  userForm: UserFormInterface;
  selectedCity: City | null;
  availableTimes: any[];
  services: any[];
  invitations: any[];
  activeTime: string | null;
  activeInvite: string | null;
  price: number;
  totalDiscount: number;
  discountedPrice: number;
  autoDiscount: number;
  discountStatus: Boolean | null;
  isPosting: boolean;
  discountRef: React.RefObject<InputRef>;
  fillableForm: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDiscountCheck: () => void;
  onSubmit: () => void;
  onPrevStep: () => void;
  apiBaseUrl: string;
  combo: boolean;
  confirmationChecked: boolean;
  onConfirmationChange: (checked: boolean) => void;
  marketingConsent: boolean;
  onMarketingConsentChange: (checked: boolean) => void;
  isFirstTimeSlotSelected: boolean;
}

const Step5: React.FC<Step5Props> = ({
  userForm,
  selectedCity,
  availableTimes,
  services,
  invitations,
  activeTime,
  activeInvite,
  price,
  totalDiscount,
  discountedPrice,
  autoDiscount,
  discountStatus,
  isPosting,
  discountRef,
  fillableForm,
  onDiscountCheck,
  onSubmit,
  onPrevStep,
  apiBaseUrl,
  combo,
  confirmationChecked,
  onConfirmationChange,
  marketingConsent,
  onMarketingConsentChange,
  isFirstTimeSlotSelected,
}) => {
  const selectedTime = availableTimes.find((time) => time.uuid === activeTime);
  const selectedServices = services.filter((service) =>
    userForm.services_selected.includes(service.uuid),
  );
  const selectedInvitation = invitations.find(
    (inv) => inv.uuid === activeInvite,
  );

  // Calculate adjusted time based on selected extension services
  const extensionMinutes = selectedServices
    .filter((s) => s.extension_minutes)
    .reduce((sum: number, s: any) => sum + s.extension_minutes, 0);

  const adjustTime = (
    timeStr: string,
    minutes: number,
    direction: "earlier" | "later",
  ): string => {
    if (!timeStr || minutes === 0) return timeStr;
    const parts = timeStr.split(":");
    const h = parseInt(parts[0]);
    const m = parseInt(parts[1]);
    const totalMinutes =
      direction === "earlier" ? h * 60 + m - minutes : h * 60 + m + minutes;
    const newH = Math.floor(totalMinutes / 60);
    const newM = totalMinutes % 60;
    return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`;
  };

  const displayStartTime = selectedTime
    ? isFirstTimeSlotSelected && extensionMinutes > 0
      ? adjustTime(selectedTime.time_start, extensionMinutes, "earlier")
      : selectedTime.time_start.substring(0, selectedTime.time_start.length - 3)
    : "";
  const displayEndTime = selectedTime
    ? !isFirstTimeSlotSelected && extensionMinutes > 0
      ? adjustTime(selectedTime.time_end, extensionMinutes, "later")
      : selectedTime.time_end.substring(0, selectedTime.time_end.length - 3)
    : "";

  const inputStyle: React.CSSProperties = {
    width: "100%",
  };

  return (
    <>
      {/* Reservation Summary */}
      <CardPartial
        isPosting={isPosting}
        title="Užsakymo santrauka"
        active_time={activeTime}
      >
        {/* Two column layout with left data and right invitation info */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            marginBottom: "8px",
          }}
        >
          {/* Left column - All main data */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div>
              <strong>Miestas:</strong> {selectedCity?.name}
            </div>
            <div>
              <strong>Data:</strong> {userForm.day_selected}
            </div>
            {selectedTime && (
              <div>
                <strong>Laikas:</strong> {displayStartTime}-{displayEndTime}
                {extensionMinutes > 0 && (
                  <span
                    style={{
                      color: "#d33092",
                      fontSize: "12px",
                      marginLeft: "4px",
                    }}
                  >
                    ({isFirstTimeSlotSelected ? "paankstinta" : "pratęsta"}{" "}
                    {extensionMinutes} min.)
                  </span>
                )}
              </div>
            )}
            <div>
              <strong>
                {userForm.is_twin_birthday
                  ? "Dvynių vardai:"
                  : userForm.is_two_children_birthday
                    ? "Jaunesnio vaiko vardas:"
                    : "Vaiko vardas:"}
              </strong>{" "}
              {userForm.customer_name}
            </div>
            <div>
              <strong>
                {userForm.is_two_children_birthday
                  ? "Jaunesnio vaiko amžius:"
                  : "Amžius:"}
              </strong>{" "}
              {userForm.customer_age}
            </div>
            {userForm.is_two_children_birthday && userForm.child_name_2 && (
              <>
                <div>
                  <strong>Vyresnio vaiko vardas:</strong>{" "}
                  {userForm.child_name_2}
                </div>
                <div>
                  <strong>Vyresnio vaiko amžius:</strong> {userForm.child_age_2}
                </div>
              </>
            )}
            <div>
              <strong>Vaikų skaičius:</strong> {userForm.customer_guests}
            </div>
            <div>
              <strong>Tėvų vardas:</strong> {userForm.customer_parent_name}
            </div>
            <div>
              <strong>Telefonas:</strong> +370{userForm.customer_phone_no}
            </div>
            <div>
              <strong>El. paštas:</strong> {userForm.customer_email}
            </div>
            <div>
              <strong>Adresas:</strong> {userForm.customer_address}
            </div>
          </div>

          {/* Right column - Invitation info */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              alignItems: "center",
            }}
          >
            <div>
              <strong>Spausdinti kvietimus:</strong>{" "}
              {userForm.print_invites === "true" ? "Taip" : "Ne"}
            </div>

            {selectedInvitation && (
              <div style={{ marginTop: "4px", textAlign: "center" }}>
                <img
                  src={`${apiBaseUrl}/api/invitations/${selectedInvitation.uuid}/thumbnail`}
                  alt="Pasirinktas kvietimas"
                  style={{ maxWidth: "120px", borderRadius: "4px" }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Services list - single column for better readability */}
        {selectedServices.length > 0 && (
          <div>
            <strong>Papildukai:</strong>
            <ul style={{ marginTop: "4px", marginBottom: "8px" }}>
              {selectedServices.map((service) => (
                <li key={service.uuid}>
                  {service.name} - {service.price}€
                </li>
              ))}
            </ul>
            {combo && (
              <div className="text-[#d33092] text-sm">
                <CheckCircleFilled /> Pasirinkus spragėsius ir cukraus vatą
                kartu taikoma -10 EUR nuolaida
              </div>
            )}
          </div>
        )}
      </CardPartial>

      {/* Discount Code Section */}
      <CardPartial
        isPosting={isPosting}
        title="Nuolaidos kodas"
        active_time={activeTime}
      >
        <Input
          ref={discountRef}
          placeholder="Nuolaidos kodas"
          name="discount_code"
          onChange={fillableForm}
          style={inputStyle}
          value={userForm.discount_code}
        />
        <div className="text-center w-full">
          <div className="mt-2">
            {discountStatus !== null &&
              (discountStatus ? (
                <span className="text-green-600">
                  <CheckCircleFilled /> Nuolaidos kodas pritaikytas
                </span>
              ) : (
                <span className="text-red-600">
                  <CloseCircleFilled /> Nuolaidos kodas nerastas
                </span>
              ))}
          </div>
          <Button
            onClick={onDiscountCheck}
            className="mt-2"
            type="primary"
            disabled={!userForm.day_selected}
          >
            Patikrinti
          </Button>
        </div>
      </CardPartial>

      {/* Payment Summary */}
      <CardPartial
        isPosting={isPosting}
        title="Mokėtina suma"
        active_time={activeTime}
      >
        <div className="w-full text-5xl text-center text-[#D33092]">
          {price.toFixed(0)}
          <sup>
            <small>EUR</small>
          </sup>
        </div>
        {totalDiscount > 0 && (
          <div className="w-full text-2xl text-center text-[#66469C]">
            Suma su nuolaida: {discountedPrice.toFixed(0)}
            <sup>
              <small>EUR</small>
            </sup>
          </div>
        )}
        {/* Visual Indicator for Automatic Discount */}
        {autoDiscount > 0 && (
          <div className="w-full text-center mt-2">
            <Badge
              count={`${autoDiscount} EUR nuolaida`}
              style={{ backgroundColor: "#52c41a" }}
            />
          </div>
        )}
        <div className="text-center w-full">
          <div
            className="mb-4 text-left"
            style={{ display: "flex", flexDirection: "column", gap: "8px" }}
          >
            <Checkbox
              checked={marketingConsent}
              onChange={(e) => onMarketingConsentChange(e.target.checked)}
            >
              <span>Sutinku gauti rinkodaros pranešimus ir pasiūlymus</span>
            </Checkbox>
            <Checkbox
              checked={confirmationChecked}
              onChange={(e) => onConfirmationChange(e.target.checked)}
            >
              Patvirtinu, kad visi duomenys yra teisingi, sutinku su{" "}
              <a
                href="https://pramoguloftas.lt/privatumo-politika/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#66469C", textDecoration: "underline" }}
              >
                privatumo politika
              </a>{" "}
              bei paslaugų teikimo sąlygomis *
            </Checkbox>
          </div>
          <div
            style={{ display: "flex", gap: "8px", justifyContent: "center" }}
          >
            <Button className="mt-5" onClick={onPrevStep}>
              Atgal
            </Button>
            <Button
              className="mt-5"
              onClick={onSubmit}
              type="primary"
              loading={isPosting}
              disabled={!confirmationChecked}
            >
              Pateikti
            </Button>
          </div>
        </div>
      </CardPartial>
    </>
  );
};

export default Step5;
